<template>
  <div>
    <v-row align="end" class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
    </v-row>
    <v-row justify="center" class="py-15" no-gutters v-if="loading">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>
    <template v-else>
      <div class="mt-6" v-for="company in filteredCompanies" :key="company.name">
        <h2>{{ toLatin(company.name) }}</h2>
        <v-data-table
          :headers="headers"
          :items="customSearch(company.meals, headers, search)"
          hide-default-footer
          disable-pagination
          class="striped-table ordera-box-shadow"
          dense
        >
          <template v-slot:item.num="{ item, index }">
            {{ index + 1 }}
          </template>
          <template v-slot:item.shift1="{ item }">
            {{ formatShiftDisplay(item.shift1) }}
          </template>
          <template v-slot:item.shift2="{ item }">
            {{ formatShiftDisplay(item.shift2) }}
          </template>
          <template v-slot:item.shift3="{ item }">
            {{ formatShiftDisplay(item.shift3) }}
          </template>
          <template v-slot:item.shift4="{ item }">
            {{ formatShiftDisplay(item.shift4) }}
          </template>
          <template v-slot:item.total="{ item }">
            {{ formatTotalDisplay(item) }}
          </template>
          <template v-slot:body.append>
            <tr class="font-weight-bold">
              <td></td>
              <td>{{ $t("total") }}</td>
              <td v-if="selectedShifts.includes(1)">
                {{ formatShiftDisplay(company.totals.shift1) }}
              </td>
              <td v-if="selectedShifts.includes(2)">
                {{ formatShiftDisplay(company.totals.shift2) }}
              </td>
              <td v-if="selectedShifts.includes(3)">
                {{ formatShiftDisplay(company.totals.shift3) }}
              </td>
              <td v-if="selectedShifts.includes(4)">
                {{ formatShiftDisplay(company.totals.shift4) }}
              </td>
              <td>{{ formatShiftDisplay(company.totals.total) }}</td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </template>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyReportTabTaken",
  mixins: [hasPrinting, hasExcel, hasReportPrinting],
  data() {
    return {
      report: null,
      loading: false,
      selectedShifts: [1, 2, 3, 4], // Po default sve četiri smene
    };
  },
  created() {
    this.fetchMeals();
  },
  methods: {
    fetchMeals() {
      this.loading = true;
      reportService
        .getMealsForRestaurant(this.restaurantId, {
          company: this.selectedCompanies,
          date: this.dates.from,
          date2: this.dates.to,
        })
        .then((response) => {
          this.report = response.data;
        })
        .catch((error) => {
          console.error("Error fetching meals:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatShiftDisplay(shiftData) {
      if (!shiftData) return "0";
      const quantity = shiftData.quantity || 0;
      const quantityPayed = shiftData.quantity_payed || 0;

      if (this.canCheckReport) {
        return `${quantity}/${quantityPayed}`;
      } else {
        return `${quantity}`;
      }
    },
    formatTotalDisplay(item) {
      const total = item.total || 0;
      const totalPayed = item.total_payed || 0;

      if (this.canCheckReport) {
        return `${totalPayed}/${total}`;
      } else {
        return `${total}`;
      }
    },
    createTotalsRowForCompany(company) {
      return {
        num: "",
        name: this.$t("total"),
        shift1: this.formatShiftDisplay(company.totals.shift1),
        shift2: this.formatShiftDisplay(company.totals.shift2),
        shift3: this.formatShiftDisplay(company.totals.shift3),
        shift4: this.formatShiftDisplay(company.totals.shift4),
        total: this.formatShiftDisplay(company.totals.total),
      };
    },
  },
  mounted() {
    this.fetchMeals();
  },
  computed: {
    companies() {
      if (!this.report) return [];

      const companiesData = [];

      for (const companyName in this.report) {
        const shifts = this.report[companyName];
        const processedMeals = {};
        const companyTotals = {
          shift1: { quantity: 0, quantity_payed: 0 },
          shift2: { quantity: 0, quantity_payed: 0 },
          shift3: { quantity: 0, quantity_payed: 0 },
          shift4: { quantity: 0, quantity_payed: 0 },
          total: { quantity: 0, quantity_payed: 0 },
        };

        for (const shiftName in shifts) {
          const mealsInShift = shifts[shiftName];
          const shiftKey = `shift${parseInt(shiftName, 10)}`;

          for (const mealName in mealsInShift) {
            const mealData = mealsInShift[mealName];

            if (!processedMeals[mealName]) {
              processedMeals[mealName] = {
                id: mealData.id,
                name: this.toLatin(mealName),
                shift1: { quantity: 0, quantity_payed: 0 },
                shift2: { quantity: 0, quantity_payed: 0 },
                shift3: { quantity: 0, quantity_payed: 0 },
                shift4: { quantity: 0, quantity_payed: 0 },
                total: 0,
                total_payed: 0,
              };
            }

            if (!processedMeals[mealName][shiftKey]) {
              processedMeals[mealName][shiftKey] = {
                quantity: 0,
                quantity_payed: 0,
              };
            }

            processedMeals[mealName][shiftKey].quantity += mealData.quantity;
            processedMeals[mealName][shiftKey].quantity_payed += mealData.quantity_payed;
            processedMeals[mealName].total += mealData.quantity;
            processedMeals[mealName].total_payed += mealData.quantity_payed;

            // Add to company totals
            companyTotals[shiftKey].quantity += mealData.quantity;
            companyTotals[shiftKey].quantity_payed += mealData.quantity_payed;
            companyTotals.total.quantity += mealData.quantity;
            companyTotals.total.quantity_payed += mealData.quantity_payed;
          }
        }

        companiesData.push({
          name: companyName,
          meals: Object.values(processedMeals),
          totals: companyTotals,
        });
      }

      return companiesData;
    },
    filteredCompanies() {
      if (!this.selectedShifts || this.selectedShifts.length === 0) {
        return this.companies;
      }

      return this.companies.map((company) => {
        const filteredMeals = company.meals.map((meal) => {
          const filteredMeal = { ...meal, total: 0, total_payed: 0 };

          // Reset shift values based on selected shifts
          filteredMeal.shift1 = this.selectedShifts.includes(1)
            ? meal.shift1
            : { quantity: 0, quantity_payed: 0 };
          filteredMeal.shift2 = this.selectedShifts.includes(2)
            ? meal.shift2
            : { quantity: 0, quantity_payed: 0 };
          filteredMeal.shift3 = this.selectedShifts.includes(3)
            ? meal.shift3
            : { quantity: 0, quantity_payed: 0 };
          filteredMeal.shift4 = this.selectedShifts.includes(4)
            ? meal.shift4
            : { quantity: 0, quantity_payed: 0 };

          // Recalculate total
          filteredMeal.total =
            filteredMeal.shift1.quantity +
            filteredMeal.shift2.quantity +
            filteredMeal.shift3.quantity +
            filteredMeal.shift4.quantity;

          filteredMeal.total_payed =
            filteredMeal.shift1.quantity_payed +
            filteredMeal.shift2.quantity_payed +
            filteredMeal.shift3.quantity_payed +
            filteredMeal.shift4.quantity_payed;

          return filteredMeal;
        });

        // Recalculate company totals based on filtered shifts
        const filteredTotals = {
          shift1: { quantity: 0, quantity_payed: 0 },
          shift2: { quantity: 0, quantity_payed: 0 },
          shift3: { quantity: 0, quantity_payed: 0 },
          shift4: { quantity: 0, quantity_payed: 0 },
          total: { quantity: 0, quantity_payed: 0 },
        };

        filteredMeals.forEach((meal) => {
          if (this.selectedShifts.includes(1)) {
            filteredTotals.shift1.quantity += meal.shift1.quantity;
            filteredTotals.shift1.quantity_payed += meal.shift1.quantity_payed;
          }
          if (this.selectedShifts.includes(2)) {
            filteredTotals.shift2.quantity += meal.shift2.quantity;
            filteredTotals.shift2.quantity_payed += meal.shift2.quantity_payed;
          }
          if (this.selectedShifts.includes(3)) {
            filteredTotals.shift3.quantity += meal.shift3.quantity;
            filteredTotals.shift3.quantity_payed += meal.shift3.quantity_payed;
          }
          if (this.selectedShifts.includes(4)) {
            filteredTotals.shift4.quantity += meal.shift4.quantity;
            filteredTotals.shift4.quantity_payed += meal.shift4.quantity_payed;
          }
          filteredTotals.total.quantity += meal.total;
          filteredTotals.total.quantity_payed += meal.total_payed;
        });

        return {
          ...company,
          meals: filteredMeals,
          totals: filteredTotals,
        };
      });
    },
    search() {
      return this.$store.state.report.filters.search;
    },
    canCheckReport() {
      const company = this.$store.state.user.user.companies[0];
      if (!company) return false;
      return !!company.checkReport;
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies
        .map((company) => company.id)
        .join(",");
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
    restaurantId() {
      return this.$store.state.company?.restaurant_id || this.$store.state.company;
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", print: false },
        { text: this.$t("meals"), value: "name" },
      ];

      const shiftHeaders = [];

      // Dodaj header kolone samo za selektovane smene
      if (this.selectedShifts.includes(1)) {
        shiftHeaders.push({ text: this.$t("shift_one"), value: "shift1" });
      }
      if (this.selectedShifts.includes(2)) {
        shiftHeaders.push({ text: this.$t("shift_two"), value: "shift2" });
      }
      if (this.selectedShifts.includes(3)) {
        shiftHeaders.push({ text: this.$t("shift_three"), value: "shift3" });
      }
      if (this.selectedShifts.includes(4)) {
        shiftHeaders.push({ text: this.$t("shift_four"), value: "shift4" });
      }

      const totalHeader = { text: this.$t("total"), value: "total" };

      return [...baseHeaders, ...shiftHeaders, totalHeader];
    },
  },
  watch: {
    "dates.from"(newVal, oldVal) {
      this.fetchMeals();
    },
    selectedCompanies(newVal, oldVal) {
      this.fetchMeals();
    },
    isPrinting(newVal, oldVal) {
      if (newVal) {
        this.printDocument({
          tables: this.filteredCompanies.map((company) => {
            const itemsWithTotals = [
              ...this.customSearch(company.meals, this.headers, this.search).map(
                (meal) => ({
                  ...meal,
                  total: this.formatTotalDisplay(meal),
                  shift1: this.formatShiftDisplay(meal.shift1),
                  shift2: this.formatShiftDisplay(meal.shift2),
                  shift3: this.formatShiftDisplay(meal.shift3),
                  shift4: this.formatShiftDisplay(meal.shift4),
                })
              ),
              this.createTotalsRowForCompany(company),
            ];

            return {
              headers: this.headers.filter((h) => h.print !== false),
              items: itemsWithTotals,
              title: this.toLatin(company.name) + " " + this.subtitle,
              showCaption: true,
            };
          }),
          title: [this.$t("report"), this.$t("taken_meals_by_companies")].join(": "),
          hideDatetime: true,
          additionalStyles: `
              .print-table {
                page-break-after: always;
              }
              .print-table:last-child {
                page-break-after: auto;
              }
            `,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName:
            [this.$t("report"), this.$t("taken_meals_by_companies")].join(" ") + ".xlsx",
          sheets: this.filteredCompanies.map((company) => {
            const itemsWithTotals = [
              ...this.customSearch(company.meals, this.headers, this.search).map(
                (meal) => ({
                  ...meal,
                  total: this.formatTotalDisplay(meal),
                  shift1: this.formatShiftDisplay(meal.shift1),
                  shift2: this.formatShiftDisplay(meal.shift2),
                  shift3: this.formatShiftDisplay(meal.shift3),
                  shift4: this.formatShiftDisplay(meal.shift4),
                })
              ),
              this.createTotalsRowForCompany(company),
            ];

            return {
              name: this.toLatin(company.name),
              headers: this.headers.filter((h) => h.print !== false),
              items: itemsWithTotals,
              title: this.toLatin(company.name),
              subtitle: this.subtitle,
            };
          }),
        });
      }
    },
  },
};
</script>
